import { useCallback, useEffect, useRef, useState } from "react";

const SCROLL_END_THRESHOLD_PX = 2;

/**
 * Tracks horizontal scroll on a table wrapper. When scrolled to the right end,
 * action columns can align naturally with the row instead of floating sticky.
 */
export function useTableHorizontalScrollEnd() {
  const scrollRef = useRef(null);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(true);

  const updateScrollEnd = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    const hasHorizontalOverflow = element.scrollWidth > element.clientWidth + SCROLL_END_THRESHOLD_PX;
    if (!hasHorizontalOverflow) {
      setIsScrolledToEnd(true);
      return;
    }

    const atEnd =
      element.scrollLeft + element.clientWidth >=
      element.scrollWidth - SCROLL_END_THRESHOLD_PX;
    setIsScrolledToEnd(atEnd);
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    updateScrollEnd();

    element.addEventListener("scroll", updateScrollEnd, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollEnd);
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener("scroll", updateScrollEnd);
      resizeObserver.disconnect();
    };
  }, [updateScrollEnd]);

  return { scrollRef, isScrolledToEnd };
}
